import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search, X } from "lucide-react";
import { INDUSTRIES, SEARCH_PAGES, SERVICES } from "../data/site";

const INDEX = [
  ...SERVICES.map((s) => ({
    name: s.name,
    path: `/services/${s.slug}`,
    group: "Services",
    keywords: `${s.description} ${s.capabilities.join(" ")}`,
  })),
  ...INDUSTRIES.map((i) => ({
    name: i.name,
    path: `/industries/${i.slug}`,
    group: "Industries",
    keywords: i.blurb,
  })),
  ...SEARCH_PAGES,
];

export default function SearchOverlay({ open, onClose, onNavigate }) {
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return INDEX.filter(
      (item) =>
        item.name.toLowerCase().includes(q) || item.keywords.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query]);

  const groups = useMemo(() => {
    const map = {};
    results.forEach((r) => {
      map[r.group] = map[r.group] || [];
      map[r.group].push(r);
    });
    return map;
  }, [results]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-gig-black/70 backdrop-blur-sm"
          onClick={onClose}
          data-testid="search-overlay"
        >
          <motion.div
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -24, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mx-auto mt-20 w-[92%] max-w-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Site search"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 border-b border-gig-steel/20 px-5 py-4">
              <Search className="h-5 w-5 text-gig-red" aria-hidden="true" />
              <input
                ref={inputRef}
                type="text"
                role="searchbox"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search services, industries, pages…"
                aria-label="Search services, industries, and pages"
                data-testid="search-input"
                className="w-full bg-transparent text-lg text-gig-black placeholder:text-gig-steel/60 focus:outline-none"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && results[0]) onNavigate(results[0].path);
                }}
              />
              <button type="button" onClick={onClose} aria-label="Close search" data-testid="search-close" className="text-gig-steel hover:text-gig-red">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto p-3" data-testid="search-results">
              {query.trim().length >= 2 && results.length === 0 && (
                <p className="px-3 py-6 text-center text-gig-steel" data-testid="search-no-results">
                  No results for “{query}”. Try “piping”, “data centers”, or “safety”.
                </p>
              )}
              {query.trim().length < 2 && (
                <p className="px-3 py-6 text-center text-sm text-gig-steel">
                  Type to search services, industries, and pages.
                </p>
              )}
              {Object.entries(groups).map(([group, items]) => (
                <div key={group} className="mb-2">
                  <p className="px-3 pt-3 pb-1 font-condensed text-xs font-semibold uppercase tracking-[0.2em] text-gig-steel">
                    {group}
                  </p>
                  {items.map((r) => (
                    <button
                      key={r.path}
                      type="button"
                      onClick={() => onNavigate(r.path)}
                      data-testid={`search-result-${r.path.replace(/\//g, "-")}`}
                      className="group flex w-full items-center justify-between px-3 py-2.5 text-left hover:bg-gig-light transition-colors"
                    >
                      <span className="font-condensed text-lg font-semibold uppercase tracking-wide text-gig-black group-hover:text-gig-red">
                        {r.name}
                      </span>
                      <ArrowRight className="h-4 w-4 text-gig-steel group-hover:text-gig-red group-hover:translate-x-1 transition-all" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
